import type { MinimalUser } from '@pbk-web/types';
import type { LexicalEditor } from 'lexical';
import { TextNode } from 'lexical';

import { CONTENT_LOAD_TAG } from '../../core/updateTags';
import { $createMentionNode, type MentionUser } from './MentionNode';

/** Each listed user's saved form, mapped to the user. */
export type MentionTokens = ReadonlyMap<string, MentionUser>;

export const mentionTokens = (users: readonly MinimalUser[], serialise: (user: MinimalUser) => string): MentionTokens =>
  new Map(
    users
      .map((user) => [serialise(user), { id: user.id, firstName: user.firstName, lastName: user.lastName }] as const)
      // An empty token would match everywhere
      .filter(([token]) => token.length > 0),
  );

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Turns saved mention text (a listed user's `serialise` output) back into `MentionNode`s as it appears.
 * - Longest token first, so one that contains another isn't cut short.
 * - Text matching no listed user stays as it is, so saving writes it back unchanged.
 */
export const registerMentionTransform = (editor: LexicalEditor, tokens: MentionTokens): (() => void) => {
  if (tokens.size === 0) return () => undefined;

  const pattern = new RegExp(
    [...tokens.keys()]
      .sort((a, b) => b.length - a.length)
      .map(escapeRegExp)
      .join('|'),
  );

  const transform = (node: TextNode) => {
    // Skips token/segmented text, which can't be split
    if (!node.isSimpleText()) return;
    const text = node.getTextContent();
    const match = pattern.exec(text);
    if (!match) return;

    const start = match.index;
    const end = start + match[0].length;
    const parts = node.splitText(...[start, end].filter((offset) => offset > 0 && offset < text.length));
    // The text after the match is a new node, so this transform runs again on it
    parts[start === 0 ? 0 : 1].replace($createMentionNode(tokens.get(match[0])!, match[0]));
  };

  let unregister: () => void = () => undefined;
  // Registering marks every text node dirty, in an update of its own. Nested in a load-tagged one, the mentions
  // it brings back count as already there, not as added (the nested update joins this one, tags included).
  editor.update(
    () => {
      unregister = editor.registerNodeTransform(TextNode, transform);
    },
    { tag: CONTENT_LOAD_TAG },
  );
  return () => unregister();
};
